import { ATLASSIAN_ACCESS_PRODUCT_ID } from '@css-xp/ppc-constants';
import { CartLinkedEntitlementsSchema, CartProductSchema, OperationType } from '../../../../types';
import { CloudSiteOrOrgNameMap } from '../../../billing-profile/src/types';
import { processEntity } from './api';

export const getValidEntry = ({
  cloudSiteOrOrgNameMapForTransfer,
  searchQuery,
  products,
  orgProductInstancesMap,
  isTWCPhase2Enabled,
  linkedEntitlements,
  isAddOnsPhase2Enabled,
}: {
  cloudSiteOrOrgNameMapForTransfer: CloudSiteOrOrgNameMap | null;
  searchQuery?: string;
  products: Record<CartProductSchema['id'], CartProductSchema>;
  orgProductInstancesMap: Record<string, CartProductSchema[]>;
  isTWCPhase2Enabled?: boolean;
  linkedEntitlements?: Record<CartLinkedEntitlementsSchema['containerOrProductId'], CartLinkedEntitlementsSchema[]>;
  isAddOnsPhase2Enabled?: boolean;
}) => {
  if (!cloudSiteOrOrgNameMapForTransfer) return null;

  // When either of FF isTWCPhase2EnabledFF or isAddOnsPhase2Enabled is not enabled, we use last data fetched for transfer
  if (isTWCPhase2Enabled || isAddOnsPhase2Enabled) {
    const processedEntity = processEntity(searchQuery);
    const searchedEntitlementOrContainerId: string[] = [];

    if (processedEntity.entitlementNumber) {
      const searchedEntitlement = Object.values(products)?.find(
        (product) => product.entitlementNumber === searchQuery && product.operationType === OperationType.TRANSFER
      );

      /**
       * If the searched entitlement is a instance entitlement, then add the parent entitlement id to the list.
       * Parent entitlement Id will add all its instances to the list.
       * Otherwise add the entitlement id or container id to the list.
       */
      const entitlementOrParentEndId = searchedEntitlement?.parentEntitlementId;
      const entitlementOrContainerId = searchedEntitlement?.containerId || searchedEntitlement?.id;
      if (entitlementOrParentEndId) {
        searchedEntitlementOrContainerId.push(entitlementOrParentEndId);
      } else if (entitlementOrContainerId) {
        searchedEntitlementOrContainerId.push(entitlementOrContainerId);
      }
    }

    if (processedEntity.siteURL) {
      // allOrgProductKey all org Ids having instances
      const allOrgProductKey = Object.keys(orgProductInstancesMap);

      // Searching if instance have same cloudsite as searched cloudsite, if so add in showing list
      const allOrgProductInstancesHavingCloudsite = allOrgProductKey.filter((key) => {
        return orgProductInstancesMap[key].some(
          (product) => product.cloudSite === searchQuery && product.operationType === OperationType.TRANSFER
        );
      });

      // All the products for searched cloudsite, find and add container ID or entitlement ID to the list
      const searchedCloudsiteProducts = Object.values(products)?.filter(
        (product) => product.cloudSite === searchQuery && product.operationType === OperationType.TRANSFER
      );

      searchedCloudsiteProducts.forEach((product) => {
        const entitlementOrContainerId = product?.containerId || product?.id;
        searchedEntitlementOrContainerId.push(entitlementOrContainerId);
      });

      /**
       * If cloudsite contains AGP entitlement, then add the parent entitlement id to the list.
       *
       * AGP entitlement is an add-on, which is stored in linkedEntitlements in store, under
       * parent (AGS) entitlement id.
       *
       * Hence, to add respective AGS entitlement id to the list, we need to check if
       * the searched cloudsite products contains AGP entitlement.
       * If so, then add the parent entitlement id to the list.
       */
      Object.values(products)?.forEach((product) => {
        if (product.operationType === OperationType.TRANSFER && product.productId === ATLASSIAN_ACCESS_PRODUCT_ID) {
          if (
            linkedEntitlements &&
            linkedEntitlements[product.id]?.[0]?.cloudSite === searchQuery &&
            linkedEntitlements[product.id]?.[0]?.operationType === OperationType.TRANSFER
          ) {
            searchedEntitlementOrContainerId.push(product.id);
          }
        }
      });

      searchedEntitlementOrContainerId.push(...allOrgProductInstancesHavingCloudsite);
    }

    // Within searchedEntitlementOrContainerId list, we have all the entitlements or container Ids for the searched entity.
    // Now we will check if the cloudsite is valid for the entitlements or container Ids.
    const validCloudsite: string[] = [];

    Object.keys(cloudSiteOrOrgNameMapForTransfer).forEach((cloudSiteHostname) => {
      /**
       * From the response cloudSiteOrOrgNameMapForTransfer, checking valid cloudsite
       */
      const isValidCloudsite = cloudSiteOrOrgNameMapForTransfer[cloudSiteHostname].products.some(
        (product) =>
          searchedEntitlementOrContainerId.includes(product.id) ||
          searchedEntitlementOrContainerId.includes(product.containerId)
      );

      if (isValidCloudsite) {
        validCloudsite.push(cloudSiteHostname);
      }
    });

    if (validCloudsite.length !== 0) {
      return Object.fromEntries(
        Object.entries(cloudSiteOrOrgNameMapForTransfer).filter(([cloudSiteHostname]) =>
          validCloudsite.includes(cloudSiteHostname)
        )
      );
    }
  }

  const cloudSiteOrOrgNameMapForTransferEntries = Object.entries(cloudSiteOrOrgNameMapForTransfer);
  const cloudSiteOrOrgNameMapForTransferLength = cloudSiteOrOrgNameMapForTransferEntries.length;
  const lastEntry = cloudSiteOrOrgNameMapForTransferEntries[cloudSiteOrOrgNameMapForTransferLength - 1];

  return { [lastEntry[0]]: lastEntry[1] };
};
